/**
 * Stale-build detection.
 *
 * When a tab is left open across a deploy, the hashed asset chunks it was
 * built against (e.g. the `wc-chunk-*.js` design-system chunks) are removed
 * from the server. A later dynamic `import()` of one of those chunks then
 * 404s and throws "Failed to fetch dynamically imported module" /
 * "Importing a module script failed" (surfacing to users as e.g. "Constructor
 * for va-alert#undefined was not found").
 *
 * This is not a real bug to alert on — no amount of retrying recovers a chunk
 * that no longer exists — so rather than report it to DataDog and leave the
 * component broken, this module confirms the running app is actually out of
 * date and, only then, silently reloads to fetch the current build. A
 * transient network failure (not a stale build) is left alone: fails closed,
 * and never reloads more than once per tab session (or at all, if
 * sessionStorage itself is unavailable — see reloadOnce).
 *
 * The running bundle already embeds a build identifier via the
 * `process.env.APP_VERSION` define (a git commit hash, see
 * config/esbuild/config.js's getAppVersion() — the same value Datadog RUM
 * already tags releases with). The server publishes the currently-deployed
 * hash at `/generated/build-manifest.json`. If the two differ, the app is
 * stale.
 *
 * @module platform/utilities/stale-build-detector
 */

import { BUILD_MANIFEST_FILENAME } from 'site/constants/build-manifest';
import environment from './environment';

/**
 * URL of the deployed build manifest. Served from /generated as a static
 * asset alongside the JS bundles. The filename comes from
 * BUILD_MANIFEST_FILENAME rather than being hardcoded here — see that
 * constant's module doc for why (this filename is spelled out in three
 * places that have to agree).
 */
export const BUILD_MANIFEST_URL = `/generated/${BUILD_MANIFEST_FILENAME}`;

/**
 * sessionStorage key so a confirmed-stale build triggers at most one reload
 * per tab session (sessionStorage clears when the tab closes) — protects
 * against a reload loop if reloading doesn't actually resolve the mismatch.
 */
export const STALE_RELOAD_FLAG = 'vagov-stale-build-reloaded';

/**
 * The build hash embedded in this bundle at build time. `process.env.APP_VERSION`
 * is 'local' for an un-deployed local build and undefined outside of an
 * esbuild-processed bundle (e.g. under Mocha), so callers can no-op safely in
 * both cases.
 * @returns {string|undefined}
 */
export function getEmbeddedBuildHash() {
  return process.env.APP_VERSION;
}

/**
 * How long to wait for the manifest request before giving up. This check
 * runs on a chunk-load failure path — often the same unstable network that
 * caused the original failure — so it must fail fast rather than delay the
 * caller's own error handling indefinitely.
 */
const MANIFEST_FETCH_TIMEOUT_MS = 3000;

/**
 * Fetch the deployed build hash from the manifest.
 * @returns {Promise<string|null>} The deployed hash, or null if unavailable.
 */
async function fetchDeployedBuildHash() {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    MANIFEST_FETCH_TIMEOUT_MS,
  );

  try {
    const response = await fetch(BUILD_MANIFEST_URL, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data?.buildHash ?? null;
  } catch {
    // Network error, timeout, JSON parse error, etc. — can't confirm staleness.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Whether the page is currently running on the origin this bundle was built
 * for (e.g. https://www.va.gov). `BUILD_MANIFEST_URL` is a same-origin
 * relative path, so on a page embedded from a different origin — notably a
 * TeamSite under the injected header/footer (see
 * src/applications/proxy-rewrite) — it would resolve against that foreign
 * origin instead. That's harmless by accident today (TeamSites don't load
 * this code), but silently comparing against, and potentially reloading, a
 * host page we don't control would be a real hazard if that ever changes.
 * `environment.BASE_URL` is already the correct per-buildtype comparison
 * value: it's hardcoded per environment (e.g. 'https://www.va.gov' for
 * vagovprod) except when running under a non-standard port, where it falls
 * back to the current location — so this is always same-origin in dev,
 * review instances, and Cypress, and only diverges for a genuine foreign-origin
 * embed.
 * @returns {boolean}
 */
export function isSameOriginAsBuild() {
  try {
    return window.location.origin === new URL(environment.BASE_URL).origin;
  } catch {
    return false;
  }
}

/**
 * Determine whether the running app is out of date relative to the deployed
 * build. Fails closed: any uncertainty (missing embedded hash, missing or
 * unreachable manifest, parse error, foreign origin) returns false so we
 * never act on a transient network failure or someone else's page.
 * @returns {Promise<boolean>} True only when we are *sure* the build is stale.
 */
export async function isBuildStale() {
  const embedded = getEmbeddedBuildHash();
  // Without a reliable local identifier we can't compare. Also skip the
  // local-dev placeholder value, which never corresponds to a real deploy.
  if (!embedded || embedded === 'local') {
    return false;
  }

  if (!isSameOriginAsBuild()) {
    return false;
  }

  const deployed = await fetchDeployedBuildHash();
  if (!deployed) {
    return false;
  }

  return deployed !== embedded;
}

/**
 * Reload the page, at most once per tab session. Fails closed: if
 * sessionStorage throws (Safari/Firefox private mode, storage disabled by
 * policy, etc.) we can't guarantee the once-per-session cap, and that failure
 * mode tends to be persistent rather than transient — reloading anyway would
 * risk an unbounded loop (each reload lands on a fresh document that hits the
 * same throw on the next terminal failure, with nothing recorded to stop it)
 * if the reload doesn't resolve the underlying mismatch. Skipping the reload
 * in that case just falls back to the pre-existing "report it" behavior.
 * @returns {boolean} True if a reload was triggered on this call.
 */
function reloadOnce() {
  try {
    if (window.sessionStorage.getItem(STALE_RELOAD_FLAG)) {
      return false;
    }
    window.sessionStorage.setItem(STALE_RELOAD_FLAG, '1');
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}

/**
 * Confirm staleness and, only if confirmed, silently reload to fetch the
 * current build. Safe to call from error paths — it never throws.
 * @returns {Promise<boolean>} True if a stale build was confirmed and a
 *   reload was triggered on this call. Callers should treat `true` as "this
 *   failure is already being recovered from" and skip reporting it.
 */
export async function reloadIfBuildStale() {
  const stale = await isBuildStale();
  if (!stale) {
    return false;
  }
  return reloadOnce();
}
